// src/operator/shared/credential-input.tsx
//
// Composant d'identification générique réutilisable. Encapsule deux modes
// d'authentification :
//   - Manuel : champs username / password / domain (selon config)
//   - VOLT   : un seul <select> qui liste les credentials du coffre, scellé
//              vers l'agent (le secret ne transite jamais en clair)
//
// Bascule par checkbox « 🔑 VOLT ». Pré-sélection automatique depuis
// vaultDefaults[usage]. Layout inline (toolbar) ou stacked (formulaire).
//
// Réutilisable pour : RDP, WatchGuard CLI/portail, SSH, PowerShell RUNAS, etc.

import { createEffect, createSignal, For, Show, type Component } from "solid-js";
import {
  listCredentialVaultCredentials,
  sealCredentialVaultCredentialForAgent,
  requestCredentialVaultDirectRdpGrant,
  type CredentialVaultCredential,
} from "./credential-vault-client";
import { createCredentialGrantKey } from "./credential-grant";
import type { AgentCredentialPublicKey, EncryptedCredential } from "./types";
import "./credential-input.css";

// ── Types publics ────────────────────────────────────────────────────────

export type CredentialUsage =
  | "rdp"
  | "watchguard-cli"
  | "watchguard-portal"
  | "ssh"
  | "powershell-runas"
  | "powershell-vmdirect";

export interface CredentialInputResult {
  mode: "manual" | "volt";
  /** En mode VOLT : secret scellé vers l'agent (prêt pour le tool agent).
   *  En mode manuel : undefined (le parent chiffre lui-même s'il le faut).
   *  Non disponible en decryptMode. */
  sealed?: EncryptedCredential;
  /** Username (manuel, ou lu depuis le credential VOLT). */
  username?: string;
  /** Password en clair.
   *  - Mode manuel : saisi par l'opérateur (à chiffrer par le parent).
   *  - Mode VOLT + decryptMode : déchiffré côté navigateur via grant key
   *    (jamais transmis au relay, zero-éffacé après usage).
   *  - Mode VOLT normal (sealMode) : undefined. */
  password?: string;
  /** Domain (manuel, ou lu depuis le credential VOLT). */
  domain?: string;
  /** ID du credential VOLT sélectionné (mode volt uniquement). */
  credentialId?: string;
  /** Prêt à être consommé ? (mode manuel: champs requis remplis ; mode volt:
   *  credential sélectionné ET scellé/déchiffré). */
  ready: boolean;
}

export interface CredentialInputProps {
  /** Clé dans vaultDefaults + filtre kind côté API VOLT. */
  usage: CredentialUsage;
  /** Préférence credential par défaut du host (pré-sélection auto). */
  vaultDefaults?: Record<string, string>;
  /** Layout horizontal (toolbar) ou vertical (formulaire). */
  layout?: "inline" | "stacked";
  /** Champs à afficher en mode manuel. */
  fields?: {
    username?: boolean;
    password?: boolean;
    domain?: "optional" | "required" | false;
  };
  /** Valeurs par défaut en mode manuel. */
  defaults?: {
    username?: string;
    domain?: string;
  };
  /** Désactive tous les champs (ex. session connectée). */
  disabled?: boolean;
  /** Fonction qui fournit la clé publique agent (pour sceller). Le composant
   *  la met en cache. */
  requestAgentKey: () => Promise<AgentCredentialPublicKey>;
  /** Notifie le parent à chaque changement de résultat. */
  onResult: (result: CredentialInputResult) => void;
  /** Label optionnel devant la checkbox VOLT. */
  voltLabel?: string;
  /** Kinds VOLT à lister dans le select (défaut: [usage, "generic"]). */
  voltKinds?: string[];
  /** Mode de récupération du secret VOLT :
   *  - "seal" (défaut) : scelle le secret vers l'agent via sealForAgent.
   *    Le secret ne transite jamais en clair. Utilisé pour les tools agent
   *    (WatchGuard, PowerShell RUNAS, etc.).
   *  - "decrypt" : déchiffre le secret côté navigateur via direct-rdp-grant
   *    + clé RSA opérateur. Le secret en clair est exposé dans result.password.
   *    Utilisé pour les clients locaux (IronRDP WASM) qui ont besoin du
   *    password en clair. Le secret est zero-éffacé après usage.
   *  Sécurité : en mode "decrypt", le secret est déchiffré localement dans le
   *  navigateur, jamais transmis au relay. La paire RSA est générée in-browser
   *  et jetée après usage. */
  decryptMode?: boolean;
  /** Paramètres additionnels pour le grant decrypt (host cible, session, etc.). */
  decryptParams?: {
    hostId?: string;
    sessionCode?: string;
    tunnelId?: string;
    peerId?: string;
  };
}

// ── Composant ────────────────────────────────────────────────────────────

export const CredentialInput: Component<CredentialInputProps> = (props) => {
  const layout = () => props.layout ?? "stacked";
  const showUsername = () => props.fields?.username !== false;
  const showPassword = () => props.fields?.password !== false;
  const domainMode = () => props.fields?.domain ?? false;

  // ── État mode manuel ──
  const [username, setUsername] = createSignal(props.defaults?.username ?? "");
  const [password, setPassword] = createSignal("");
  const [domain, setDomain] = createSignal(props.defaults?.domain ?? "");

  // ── État VOLT ──
  const [voltEnabled, setVoltEnabled] = createSignal(false);
  const [voltCreds, setVoltCreds] = createSignal<CredentialVaultCredential[]>([]);
  const [voltId, setVoltId] = createSignal("");
  const [voltLoading, setVoltLoading] = createSignal(false);
  let voltSelectRef: HTMLSelectElement | undefined;
  let agentKeyCache: AgentCredentialPublicKey | null = null;
  // Credential résolu (cache). En sealMode : { id, sealed }. En decryptMode :
  // { id, password (clair) }. Mis à jour par resolveInBackground.
  const [resolvedCred, setResolvedCred] = createSignal<{
    id: string;
    sealed?: EncryptedCredential;
    password?: string;
  } | null>(null);

  // ── Chargement des credentials VOLT ──
  async function refreshVoltCreds() {
    if (!voltEnabled()) {
      setVoltCreds([]);
      return;
    }
    setVoltLoading(true);
    try {
      const kinds = props.voltKinds ?? [props.usage, "generic"];
      const all: CredentialVaultCredential[] = [];
      for (const kind of kinds) {
        const res = await listCredentialVaultCredentials({ kind: kind as never });
        all.push(...(res.items ?? []));
      }
      // Déduplique par id.
      const seen = new Set<string>();
      setVoltCreds(all.filter((c) => (seen.has(c.id) ? false : seen.add(c.id))));
    } catch {
      setVoltCreds([]);
    } finally {
      setVoltLoading(false);
    }
  }

  // ── Pré-sélection depuis vaultDefaults ──
  // On charge d'abord la liste (async), puis on applique l'ID pour que le
  // <select> ait l'<option> correspondante.
  let defaultsApplied = false;
  createEffect(() => {
    const defs = props.vaultDefaults;
    if (!defs || defaultsApplied) return;
    const id = defs[props.usage];
    if (!id) return;
    defaultsApplied = true;
    setVoltEnabled(true);
    void (async () => {
      await refreshVoltCreds();
      setVoltId(id);
    })();
  });

  // ── Synchronisation forcée du <select> via ref ──
  // SolidJS ne met pas à jour le DOM du select si la valeur est posée avant
  // que les <option> n'existent. On force la synchro en réaction à la liste
  // ET à la valeur.
  createEffect(() => {
    const _list = voltCreds();
    const id = voltId();
    queueMicrotask(() => {
      if (voltSelectRef && id && voltSelectRef.value !== id) voltSelectRef.value = id;
    });
  });

  // ── Émission du résultat ──
  // Synchrone : utilise le cache resolvedCred() si disponible, sinon émet
  // ready:false et déclenche la résolution async en arrière-plan (scellement
  // ou déchiffrement selon decryptMode).
  function emitResult() {
    if (voltEnabled() && voltId()) {
      const id = voltId();
      const cached = resolvedCred();
      if (cached && cached.id === id) {
        const cred = voltCreds().find((c) => c.id === id);
        props.onResult({
          mode: "volt",
          sealed: cached.sealed,
          password: cached.password,
          username: cred?.username ?? undefined,
          domain: cred?.domain ?? undefined,
          credentialId: id,
          ready: true,
        });
        return;
      }
      // Pas encore résolu : on émet ready:false et lance la résolution.
      props.onResult({ mode: "volt", credentialId: id, ready: false });
      void resolveInBackground(id);
    } else {
      // Mode manuel.
      const ready = Boolean(
        (!showUsername() || username().trim()) &&
        (!showPassword() || password()) &&
        (domainMode() !== "required" || domain().trim()),
      );
      props.onResult({
        mode: "manual",
        username: showUsername() ? username().trim() : undefined,
        password: showPassword() ? password() : undefined,
        domain: domainMode() ? domain().trim() : undefined,
        ready,
      });
    }
  }

  // Résout le credential en arrière-plan et met à jour le cache. L'effect
  // emitResult se re-déclenchera via resolvedCred().
  // - sealMode (défaut) : scelle vers l'agent via sealForAgent
  // - decryptMode : déchiffre côté navigateur via direct-rdp-grant + clé RSA
  async function resolveInBackground(id: string) {
    try {
      if (props.decryptMode) {
        // ── decryptMode : déchiffrement côté navigateur ──
        // Le secret est déchiffré localement via une paire RSA générée
        // in-browser, jamais transmis au relay. Zero-éffacé après usage
        // par le parent (qui reçoit result.password).
        const grantKey = await createCredentialGrantKey();
        const grant = await requestCredentialVaultDirectRdpGrant(id, {
          publicKey: grantKey.publicKey,
          ...props.decryptParams,
        });
        const plaintext = await grantKey.decryptCredential(grant.credential!);
        setResolvedCred({ id, password: plaintext });
      } else {
        // ── sealMode (défaut) : scellement vers l'agent ──
        if (!agentKeyCache) agentKeyCache = await props.requestAgentKey();
        const sealed = await sealCredentialVaultCredentialForAgent(id, {
          agentPublicKey: agentKeyCache,
          purpose: props.usage,
          ttlSeconds: 60,
        });
        const enc =
          (sealed as { sealed?: EncryptedCredential; credential?: EncryptedCredential }).sealed ??
          (sealed as { credential?: EncryptedCredential }).credential;
        if (enc) setResolvedCred({ id, sealed: enc });
      }
    } catch {
      // La résolution a échoué (ex: agent hors ligne, grant refusé). Le
      // résultat reste ready:false — l'opérateur verra le message d'erreur.
    }
  }

  // Re-émet le résultat à chaque changement d'état pertinent, y compris quand
  // la résolution async termine (via resolvedCred()).
  createEffect(() => {
    void username();
    void password();
    void domain();
    void voltId();
    void voltEnabled();
    void resolvedCred();
    emitResult();
  });

  // ── Classes CSS selon layout ──
  const containerClass = () => `credential-input credential-input--${layout()}`;
  const fieldClass = () => (layout() === "inline" ? "credential-input__field-inline" : "credential-input__field");

  return (
    <div class={containerClass()}>
      {/* ── Champs manuels (masqués si VOLT coché) ── */}
      <Show when={!voltEnabled()}>
        <Show when={showUsername()}>
          <label class={fieldClass()}>
            <Show when={layout() === "stacked"}>
              <span>Identifiant</span>
            </Show>
            <input
              type="text"
              autocomplete="off"
              placeholder={layout() === "inline" ? "identifiant" : undefined}
              value={username()}
              disabled={props.disabled}
              onInput={(e) => setUsername(e.currentTarget.value)}
            />
          </label>
        </Show>
        <Show when={showPassword()}>
          <label class={fieldClass()}>
            <Show when={layout() === "stacked"}>
              <span>Mot de passe</span>
            </Show>
            <input
              type="password"
              autocomplete="off"
              placeholder={layout() === "inline" ? "mot de passe" : undefined}
              value={password()}
              disabled={props.disabled}
              onInput={(e) => setPassword(e.currentTarget.value)}
            />
          </label>
        </Show>
        <Show when={domainMode() !== false}>
          <label class={fieldClass()}>
            <Show when={layout() === "stacked"}>
              <span>Domaine{domainMode() === "optional" ? " (optionnel)" : ""}</span>
            </Show>
            <input
              type="text"
              autocomplete="off"
              placeholder={layout() === "inline" ? "domaine" : undefined}
              value={domain()}
              disabled={props.disabled}
              onInput={(e) => setDomain(e.currentTarget.value)}
            />
          </label>
        </Show>
      </Show>

      {/* ── Select VOLT (visible si VOLT coché) ── */}
      <Show when={voltEnabled()}>
        <label class={fieldClass()}>
          <Show when={layout() === "stacked"}>
            <span>Credential VOLT</span>
          </Show>
          <select
            ref={voltSelectRef}
            value={voltId()}
            disabled={props.disabled || voltLoading()}
            onChange={(e) => setVoltId(e.currentTarget.value)}
            title="Credential du coffre VOLT"
          >
            <option value="">{voltLoading() ? "Chargement…" : "— Sélectionner —"}</option>
            <For each={voltCreds()}>{(c) => <option value={c.id}>{c.label || c.username || c.id}</option>}</For>
          </select>
        </label>
      </Show>

      {/* ── Checkbox VOLT ── */}
      <label class="credential-input__volt-toggle">
        <input
          type="checkbox"
          checked={voltEnabled()}
          disabled={props.disabled}
          onChange={(e) => {
            setVoltEnabled(e.currentTarget.checked);
            if (e.currentTarget.checked) void refreshVoltCreds();
          }}
        />
        <span>{props.voltLabel ?? "🔑 VOLT"}</span>
      </label>
    </div>
  );
};
